<template>
  <div class="markdown-content" v-html="compiledMarkdown" @click="handleClick"></div>
</template>

<script>
const RE_HEADING = /^(#{1,6})\s+(.+)$/;
const RE_UL_ITEM = /^(\s*)([-*+])\s+(.+)$/;
const RE_OL_ITEM = /^(\s*)(\d+)\.\s+(.+)$/;
const RE_BLOCKQUOTE = /^>\s?(.*)$/;
const RE_HR = /^(?:---+|\*\*\*+|___+)\s*$/;

const RE_INLINE_CODE = /`([^`]+)`/g;
const RE_BOLD_STAR = /\*\*(.+?)\*\*/g;
const RE_BOLD_UNDER = /__(.+?)__/g;
const RE_ITALIC_STAR = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g;
const RE_ITALIC_UNDER = /(?<![a-zA-Z0-9])_(?!_)(.+?)(?<!_)_(?![a-zA-Z0-9_])/g;
const RE_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;
const RE_LINE_BREAK = /\n/g;

const SAFE_URL_RE = /^(?:https?:|mailto:|tel:|#|\/)/i;

function escapeHtml(str) {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function sanitizeUrl(url) {
  const trimmed = url.trim();
  return SAFE_URL_RE.test(trimmed) ? trimmed : "";
}

export default {
  name: "Markdown",
  props: {
    content: {
      type: String,
      required: true,
    },
  },
  computed: {
    compiledMarkdown() {
      return this.parseMarkdown(this.content);
    },
  },
  methods: {
    handleClick(event) {
      const anchor = event.target.closest("a");
      if (anchor && this.$el.contains(anchor)) {
        event.preventDefault();
        this.$emit("onLinkClick", { link: anchor.href });
      }
    },

    parseMarkdown(markdown) {
      if (!markdown) return "";

      const lines = markdown.split("\n");
      const blocks = [];
      let currentBlock = { type: null, content: [] };

      const flushBlock = () => {
        if (
          currentBlock.type &&
          (currentBlock.content.length > 0 || currentBlock.items?.length > 0)
        ) {
          blocks.push(currentBlock);
        }
        currentBlock = { type: null, content: [] };
      };

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();

        if (trimmedLine === "") {
          if (
            currentBlock.type &&
            currentBlock.type !== "ul" &&
            currentBlock.type !== "ol"
          ) {
            flushBlock();
          }
          continue;
        }

        if (trimmedLine.startsWith("```")) {
          if (currentBlock.type === "code") {
            blocks.push(currentBlock);
            currentBlock = { type: null, content: [] };
          } else {
            flushBlock();
            currentBlock = {
              type: "code",
              content: [],
              language: trimmedLine.slice(3).trim(),
            };
          }
          continue;
        }

        if (currentBlock.type === "code") {
          currentBlock.content.push(line);
          continue;
        }

        if (RE_HR.test(trimmedLine)) {
          flushBlock();
          blocks.push({ type: "hr", content: [] });
          continue;
        }

        const headingMatch = line.match(RE_HEADING);
        if (headingMatch) {
          flushBlock();
          blocks.push({
            type: "heading",
            level: headingMatch[1].length,
            content: [headingMatch[2]],
          });
          continue;
        }

        const bqMatch = line.match(RE_BLOCKQUOTE);
        if (bqMatch) {
          if (currentBlock.type !== "blockquote") {
            flushBlock();
            currentBlock = { type: "blockquote", content: [] };
          }
          currentBlock.content.push(bqMatch[1]);
          continue;
        }

        const ulMatch = line.match(RE_UL_ITEM);
        if (ulMatch) {
          if (currentBlock.type !== "ul") {
            flushBlock();
            currentBlock = { type: "ul", content: [], items: [] };
          }
          currentBlock.items.push({ indent: ulMatch[1].length, text: ulMatch[3] });
          continue;
        }

        const olMatch = line.match(RE_OL_ITEM);
        if (olMatch) {
          if (currentBlock.type !== "ol") {
            flushBlock();
            currentBlock = { type: "ol", content: [], items: [] };
          }
          currentBlock.items.push({ indent: olMatch[1].length, text: olMatch[3] });
          continue;
        }

        if (currentBlock.type === null || currentBlock.type === "paragraph") {
          currentBlock.type = "paragraph";
          currentBlock.content.push(line);
        } else {
          flushBlock();
          currentBlock = { type: "paragraph", content: [line] };
        }
      }

      if (
        currentBlock.type &&
        (currentBlock.content.length > 0 || currentBlock.items?.length > 0)
      ) {
        blocks.push(currentBlock);
      }

      return blocks.map((block) => this.renderBlock(block)).join("");
    },

    renderBlock(block) {
      switch (block.type) {
        case "heading": {
          return `<h${block.level}>${this.parseInline(block.content[0])}</h${block.level}>`;
        }
        case "code": {
          const escaped = escapeHtml(block.content.join("\n"));
          const langAttr = block.language
            ? ` class="language-${escapeHtml(block.language)}"`
            : "";
          return `<pre><code${langAttr}>${escaped}</code></pre>`;
        }
        case "ul": {
          return `<ul>${this.renderListItems(block.items, "ul")}</ul>`;
        }
        case "ol": {
          return `<ol>${this.renderListItems(block.items, "ol")}</ol>`;
        }
        case "blockquote": {
          const inner = block.content.join("\n");
          return `<blockquote><p>${this.parseInline(inner)}</p></blockquote>`;
        }
        case "hr": {
          return "<hr>";
        }
        case "paragraph": {
          return `<p>${this.parseInline(block.content.join("\n"))}</p>`;
        }
        default:
          return "";
      }
    },

    renderListItems(items, listTag) {
      if (!items || items.length === 0) return "";

      const baseIndent = items[0].indent;
      let html = "";
      let i = 0;

      while (i < items.length) {
        const item = items[i];

        if (item.indent <= baseIndent) {
          const children = [];
          let j = i + 1;
          while (j < items.length && items[j].indent > baseIndent) {
            children.push(items[j]);
            j++;
          }

          if (children.length > 0) {
            html += `<li>${this.parseInline(item.text)}<${listTag}>${this.renderListItems(children, listTag)}</${listTag}></li>`;
          } else {
            html += `<li>${this.parseInline(item.text)}</li>`;
          }
          i = j;
        } else {
          html += `<li>${this.parseInline(item.text)}</li>`;
          i++;
        }
      }

      return html;
    },

    parseInline(text) {
      if (!text) return "";

      const codePlaceholders = [];
      let result = text.replace(RE_INLINE_CODE, (_, code) => {
        const idx = codePlaceholders.length;
        codePlaceholders.push(`<code>${escapeHtml(code)}</code>`);
        return `\x00C${idx}\x00`;
      });

      const linkPlaceholders = [];
      result = result.replace(RE_LINK, (_, label, url) => {
        const idx = linkPlaceholders.length;
        const safeUrl = sanitizeUrl(url);
        if (safeUrl) {
          linkPlaceholders.push(
            `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`
          );
        } else {
          linkPlaceholders.push(escapeHtml(label));
        }
        return `\x00L${idx}\x00`;
      });

      result = escapeHtml(result);

      result = result.replace(RE_BOLD_STAR, "<strong>$1</strong>");
      result = result.replace(RE_BOLD_UNDER, "<strong>$1</strong>");
      result = result.replace(RE_ITALIC_STAR, "<em>$1</em>");
      result = result.replace(RE_ITALIC_UNDER, "<em>$1</em>");

      result = result.replace(RE_LINE_BREAK, "<br>");

      codePlaceholders.forEach((html, idx) => {
        result = result.replace(`\x00C${idx}\x00`, html);
      });
      linkPlaceholders.forEach((html, idx) => {
        result = result.replace(`\x00L${idx}\x00`, html);
      });

      return result;
    },
  },
};
</script>

<style>
.markdown-content {
  line-height: 1.7;
  color: inherit;
  font-size: 15px;
}

.markdown-content > *:first-child {
  margin-top: 0;
}

.markdown-content > *:last-child {
  margin-bottom: 0;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  font-weight: 600;
  line-height: 1.3;
  margin-top: 1.5em;
  margin-bottom: 0.75em;
}

.markdown-content h1 {
  font-size: 1.2em;
  margin-top: 0;
  padding-bottom: 0.3em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.markdown-content h2 {
  font-size: 1.15em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.markdown-content h3 {
  font-size: 1.05em;
}

.markdown-content h4 {
  font-size: 0.95em;
}

.markdown-content h5 {
  font-size: 0.85em;
}

.markdown-content h6 {
  font-size: 0.75em;
  color: rgba(0, 0, 0, 0.6);
}

.markdown-content p {
  font-weight: normal;
  margin: 1em 0;
  word-wrap: break-word;
}

.markdown-content ul,
.markdown-content ol {
  margin: 1em 0 1em 1.2em;
  padding-left: 5px;
}

.markdown-content ul {
  list-style-type: disc;
}

.markdown-content ol {
  list-style-type: decimal;
}

.markdown-content li {
  margin: 0.2em 0;
  line-height: 1.7;
}

.markdown-content li > p {
  margin: 0.5em 0;
}

.markdown-content li > ul,
.markdown-content li > ol {
  margin: 0.5em 0 0.5em 0.5em;
}

.markdown-content ul ul,
.markdown-content ol ul {
  list-style-type: circle;
}

.markdown-content ul ul ul,
.markdown-content ol ul ul,
.markdown-content ol ol ul {
  list-style-type: square;
}

.markdown-content ol ol {
  list-style-type: lower-alpha;
}

.markdown-content ol ol ol {
  list-style-type: lower-roman;
}

.markdown-content code {
  background: rgba(0, 0, 0, 0.05);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: "Courier New", Courier, monospace;
  font-size: 0.9em;
  white-space: pre-wrap;
  word-break: break-word;
}

.markdown-content pre {
  background: rgba(0, 0, 0, 0.05);
  padding: 1em 1.25em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1.5em 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.markdown-content pre code {
  background: none;
  padding: 0;
  font-size: 0.875em;
  line-height: 1.6;
  white-space: pre;
  word-break: normal;
}

.markdown-content a {
  color: var(--sp-chat-primary);
  text-decoration: none;
  transition: color 0.2s ease;
  position: relative;
}

.markdown-content a:after {
  content: "\2197";
  display: inline-block;
  margin-left: 0.25em;
  font-size: 0.85em;
  opacity: 0.7;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.markdown-content a:hover {
  color: var(--sp-chat-primary);
}

.markdown-content a:hover:after {
  transform: translate(2px, -2px);
  opacity: 1;
}

.markdown-content strong {
  font-weight: 600;
}

.markdown-content em {
  font-style: italic;
}

.markdown-content blockquote {
  margin: 1em 0;
  padding: 0.5em 1em;
  border-left: 4px solid rgba(0, 0, 0, 0.2);
  color: rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.02);
}

.markdown-content hr {
  border: none;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  margin: 2em 0;
}

.markdown-content h1 + p,
.markdown-content h2 + p,
.markdown-content h3 + p,
.markdown-content h4 + p,
.markdown-content h5 + p,
.markdown-content h6 + p {
  margin-top: 0.5em;
}

.markdown-content p + ul,
.markdown-content p + ol {
  margin-top: 0.5em;
}

.markdown-content ul + p,
.markdown-content ol + p {
  margin-top: 1em;
}

.markdown-content pre + p,
.markdown-content p + pre {
  margin-top: 1.5em;
}
</style>
